/**
 * Uniwersalny resolver dla danych wielojęzycznych i zagnieżdżonych.
 * Obsługuje: stringi, {pl, en}, oraz {pl: {short, long}}
 */

import { humanizeAnswerTokens } from '../config/answerValueLabels.js';

/** App locales only — i18n may be `pl-PL` / `en-US`. */
export type AppLang = 'en' | 'pl';

function displayText(value: unknown, lang: string): string {
  return humanizeAnswerTokens(value == null ? '' : String(value), lang);
}

export function normalizeLang(lang: string | undefined | null): AppLang {
  if (!lang || typeof lang !== 'string') return 'en';
  const base = lang.trim().toLowerCase().split(/[-_]/)[0];
  return base === 'pl' ? 'pl' : 'en';
}

export function isPlLang(lang: string | undefined | null): boolean {
  return normalizeLang(lang) === 'pl';
}

export const resolveValue = (data: any, lang: string = 'en') => {
    if (!data) return '';

    // 1. Jeśli to bezpośrednio string (np. stare dane)
    if (typeof data === 'string') return displayText(data, lang);

    const code = normalizeLang(lang);

    // 2. Jeśli to obiekt językowy { pl: ..., en: ... }
    // Prefer exact key, then normalized app lang, then en/pl fallbacks.
    const localized =
        data[lang] ??
        data[code] ??
        data['en'] ??
        data['pl'];

    if (localized !== undefined && localized !== null) {
        // 3. Obsługa zagnieżdżenia { short, long } - typowe dla technologii
        if (typeof localized === 'object' && localized !== null) {
            return displayText(localized.long || localized.short || Object.values(localized)[0] || '', lang);
        }
        return displayText(localized, lang);
    }

    // 4. Fallback: jeśli to inny obiekt, spróbuj zwrócić pierwszą wartość string
    if (typeof data === 'object') {
        const firstVal = Object.values(data).find(v => typeof v === 'string');
        return displayText(firstVal || '', lang);
    }

    return displayText(data, lang);
};
